import { defineStore } from 'pinia'
import { useRequest }               from 'composables/useRequest.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'

const CACHE_TTL = 10 * 60 * 1000 // 10 minutes

export const useCalendarStore = defineStore('calendar', {
  state: () => ({
    // Bookings indexed by id for O(1) real-time mutations
    bookingsById: {},

    // Track which date ranges have been loaded to avoid double-fetching
    // Array of { start: ISOString, end: ISOString }
    loadedRanges: [],

    // Tasks overlaid on the grid, Google-Calendar-Tasks style — separate
    // cache from bookings (different API, different shape) but same
    // range-loading strategy.
    tasksById: {},
    loadedTaskRanges: [],
    showTasksOnCalendar: localStorage.getItem('calendar_show_tasks') !== 'false',
    tasksLoading: false,
    tasksError: null,

    // Host events (booking type list for the create-booking form)
    hostEvents: [],
    hostEventsLoadedAt: null,

    // Connected calendars (sidebar list with color pickers)
    connectedCalendars: [],
    connectedCalendarsLoadedAt: null,

    // UI state
    visibleCalendarIds: [],       // array of calendar IDs currently shown (not a Set — Pinia can't serialize Sets)
    selectedEventFilter: 'all',   // 'all' or an event_id string
    currentView: 'week',          // 'day' | 'week' | 'month' | '4days' | 'year'
    viewInitialized: false,        // true after default_view from settings has been applied once
    selectedDate: null,           // null means today

    // Slide-over panels
    settingsPanelOpen: false,
    bookingSlideOverOpen: false,
    bookingSlideOverBooking: null,  // null = create mode, object = edit mode

    // Event popover
    eventPopoverOpen: false,
    eventPopoverBooking: null,
    eventPopoverAnchor: null,  // { top, left, right, bottom, width, height } from getBoundingClientRect

    // Full search results overlay (opened by pressing Enter in the header
    // search box) — replaces the calendar grid with a Google-Calendar-style
    // chronological list, as opposed to the small proximity-sorted autofill
    // dropdown shown while typing.
    searchOverlayOpen: false,
    searchOverlayQuery: '',
    searchOverlayResults: [],
    searchOverlayLoading: false,

    // Where overlays (popover, slide-over, settings panel) teleport to.
    // Normally 'body'; switched to the fullscreen element while native
    // fullscreen is active, because content outside it is not rendered.
    overlayTarget: 'body',

    // ActionCable connection status
    connectionStatus: 'connecting', // 'connected' | 'disconnected' | 'reconnecting'

    // Loading / error
    bookingsLoading: false,
    bookingsError: null,
    hostEventsLoading: false,
    hostEventsError: null,
    connectedCalendarsLoading: false,
  }),

  getters: {
    bookings: (state) => Object.values(state.bookingsById),

    filteredBookings: (state) => {
      const settings = useCalendarSettingsStore()
      const now      = new Date()

      let bookings = Object.values(state.bookingsById).filter(b => {
        const calendarVisible =
          !b.calendar_id ||
          state.visibleCalendarIds.length === 0 ||
          state.visibleCalendarIds.includes(b.calendar_id)
        const eventVisible =
          state.selectedEventFilter === 'all' ||
          b.event_id === state.selectedEventFilter
        return calendarVisible && eventVisible
      })

      if (!settings.show_past_bookings) {
        bookings = bookings.filter(b => new Date(b.end_at ?? b.end) > now)
      }

      if (!settings.show_declined_bookings) {
        bookings = bookings.filter(b => b.status !== 'declined')
      }

      if (settings.collapse_duplicate_events) {
        const seen = new Set()
        bookings = bookings.filter(b => {
          const key = `${b.title ?? b.name}|${b.start_at ?? b.start}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      }

      return bookings
    },

    isRangeLoaded: (state) => (start, end) => {
      return state.loadedRanges.some(r => r.start <= start && r.end >= end)
    },

    tasks: (state) => Object.values(state.tasksById),

    // Only surfaces doable tasks with a due date — canceled ones stay
    // hidden on the grid the same way canceled bookings would.
    filteredTasks: (state) => {
      if (!state.showTasksOnCalendar) return []
      return Object.values(state.tasksById).filter(t => t.due_at && t.status !== 'canceled')
    },

    isTaskRangeLoaded: (state) => (start, end) => {
      return state.loadedTaskRanges.some(r => r.start <= start && r.end >= end)
    },

    hostEventsIsFresh: (state) =>
      state.hostEventsLoadedAt !== null &&
      Date.now() - state.hostEventsLoadedAt < CACHE_TTL,

    connectedCalendarsIsFresh: (state) =>
      state.connectedCalendarsLoadedAt !== null &&
      Date.now() - state.connectedCalendarsLoadedAt < CACHE_TTL,

    // Deactivated calendars/sub-calendars are never shown on the /calendar
    // page — the API returns everything, so this is filtered client-side.
    activeConnectedCalendars: (state) =>
      state.connectedCalendars
        .filter(c => c.active)
        .map(c => ({ ...c, sub_calendars: (c.sub_calendars || []).filter(s => s.active) })),
  },

  actions: {
    // ── Initial load ──────────────────────────────────────────────

    async fetchInitialData() {
      await Promise.all([
        this.fetchHostEventsIfStale(),
        this.fetchConnectedCalendarsIfStale(),
      ])
    },

    // ── Bookings ──────────────────────────────────────────────────

    async fetchBookings(start, end) {
      if (this.isRangeLoaded(start, end)) return

      this.bookingsLoading = true
      this.bookingsError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest(
          'GET',
          `/api/v1/calendar/bookings?start_date=${start}&end_date=${end}`,
          {},
          false
        )
        if (!res.js_error && res.response?.success) {
          const bookings = res.response.data?.bookings ?? res.response.data
          if (Array.isArray(bookings)) {
            bookings.forEach(b => { this.bookingsById[b.id] = b })
          }
          this.loadedRanges.push({ start, end })
        } else {
          this.bookingsError = res.response?.message || 'Failed to load bookings.'
        }
      } catch {
        this.bookingsError = 'Something went wrong loading your calendar.'
      } finally {
        this.bookingsLoading = false
      }
    },

    // Real-time mutations — called by the ActionCable composable
    addBooking(booking)    { this.bookingsById[booking.id] = booking },
    updateBooking(booking) {
      if (this.bookingsById[booking.id]) {
        this.bookingsById[booking.id] = { ...this.bookingsById[booking.id], ...booking }
      }
    },
    removeBooking(id)      { delete this.bookingsById[id] },

    updateBookingNotes(id, notes) {
      const b = this.bookingsById[id]
      if (b) b.internal_notes = notes
    },

    // A note was applied to a whole recurring series — patch every visible instance
    updateSeriesNotes(recurringBookingId, calendarId, notes) {
      if (!recurringBookingId) return
      Object.values(this.bookingsById).forEach(b => {
        if (b.recurring_booking_id === recurringBookingId && b.calendar_id === calendarId) {
          b.internal_notes = notes
        }
      })
    },

    // The owner's RSVP changed (Going? Yes/No/Maybe) — keep the cached booking
    // consistent so chip styling (stripes, strikethrough) updates immediately.
    updateBookingRsvp(id, status) {
      const b = this.bookingsById[id]
      if (!b) return
      b.calendar_owner_status = status
      const self = b.attendees?.find(a => a.self)
      if (self) self.response_status = status
    },

    invalidate() {
      this.loadedRanges = []
      this.bookingsById = {}
      this.bookingsError = null
    },

    // ── Tasks (Google-Calendar-Tasks-style overlay) ─────────────────

    async fetchTasks(start, end) {
      if (this.isTaskRangeLoaded(start, end)) return

      this.tasksLoading = true
      this.tasksError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/tasks?start_date=${start}&end_date=${end}`, {}, false)
        if (!res.js_error && res.response?.success) {
          res.response.data.forEach(t => { this.tasksById[t.id] = t })
          this.loadedTaskRanges.push({ start, end })
        } else {
          this.tasksError = res.response?.message || 'Failed to load tasks.'
        }
      } catch {
        this.tasksError = 'Something went wrong loading your tasks.'
      } finally {
        this.tasksLoading = false
      }
    },

    updateTask(task) {
      if (task?.id) this.tasksById[task.id] = { ...this.tasksById[task.id], ...task }
    },

    removeTask(id) { delete this.tasksById[id] },

    toggleTasksVisibility() {
      this.showTasksOnCalendar = !this.showTasksOnCalendar
      localStorage.setItem('calendar_show_tasks', String(this.showTasksOnCalendar))
    },

    invalidateTasks() {
      this.loadedTaskRanges = []
      this.tasksById = {}
      this.tasksError = null
    },

    // ── Host events ───────────────────────────────────────────────

    async fetchHostEventsIfStale() {
      if (this.hostEventsIsFresh && this.hostEvents.length) return
      await this.fetchHostEvents()
    },

    async fetchHostEvents() {
      this.hostEventsLoading = true
      this.hostEventsError = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/events', {}, false)
        if (!res.js_error && res.response?.success) {
          this.hostEvents = res.response.data
          this.hostEventsLoadedAt = Date.now()
        } else {
          this.hostEventsError = res.response?.message || 'Failed to load events.'
        }
      } catch {
        this.hostEventsError = 'Something went wrong.'
      } finally {
        this.hostEventsLoading = false
      }
    },

    // ── Connected calendars ───────────────────────────────────────

    async fetchConnectedCalendarsIfStale() {
      if (this.connectedCalendarsIsFresh && this.connectedCalendars.length) return
      await this.fetchConnectedCalendars()
    },

    async fetchConnectedCalendars() {
      this.connectedCalendarsLoading = true
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/connected_calendars', {}, false)
        if (!res.js_error && res.response?.success) {
          this.connectedCalendars = res.response.data
          this.connectedCalendarsLoadedAt = Date.now()
          // Initialize visibility: show all *active* calendars (parents + sub-calendars)
          // by default. Deactivated ones are deliberately left out so their bookings
          // stay hidden via the calendarVisible check in filteredBookings.
          if (this.visibleCalendarIds.length === 0) {
            const allIds = []
            res.response.data.forEach(c => {
              if (!c.active) return
              allIds.push(c.id)
              if (c.sub_calendars) c.sub_calendars.forEach(s => { if (s.active) allIds.push(s.id) })
            })
            this.visibleCalendarIds = allIds
          }
        }
      } finally {
        this.connectedCalendarsLoading = false
      }
    },

    // ── UI actions ────────────────────────────────────────────────

    setView(view)              { this.currentView = view },
    setDate(date)              { this.selectedDate = date },
    setConnectionStatus(s)     { this.connectionStatus = s },
    setEventFilter(id)         { this.selectedEventFilter = id || 'all' },

    toggleCalendarVisibility(id) {
      const idx = this.visibleCalendarIds.indexOf(id)
      if (idx === -1) {
        this.visibleCalendarIds.push(id)
      } else {
        this.visibleCalendarIds.splice(idx, 1)
      }
    },

    openSettingsPanel()  { this.settingsPanelOpen = true },
    closeSettingsPanel() { this.settingsPanelOpen = false },

    setOverlayTarget(target) { this.overlayTarget = target || 'body' },

    openEventPopover(booking, anchorRect) {
      this.eventPopoverBooking = booking
      this.eventPopoverAnchor  = anchorRect
        ? { top: anchorRect.top, left: anchorRect.left, right: anchorRect.right, bottom: anchorRect.bottom, width: anchorRect.width, height: anchorRect.height }
        : null
      this.eventPopoverOpen = true
    },
    closeEventPopover() {
      this.eventPopoverOpen   = false
      this.eventPopoverBooking = null
      this.eventPopoverAnchor  = null
    },

    openBookingSlideOver(booking = null) {
      this.bookingSlideOverBooking = booking
      this.bookingSlideOverOpen = true
    },
    closeBookingSlideOver() {
      this.bookingSlideOverOpen = false
      this.bookingSlideOverBooking = null
    },

    async openSearchOverlay(query, { from_date, to_date } = {}) {
      this.searchOverlayOpen = true
      this.searchOverlayQuery = query
      this.searchOverlayLoading = true
      try {
        const { axiosRequest } = useRequest()
        const params = new URLSearchParams({ query, mode: 'full' })
        if (from_date) params.set('from_date', from_date)
        if (to_date) params.set('to_date', to_date)
        const res = await axiosRequest('GET', `/api/v1/calendar/bookings/search?${params.toString()}`, {}, false)
        this.searchOverlayResults = (!res.js_error && res.response?.success) ? res.response.data : []
      } finally {
        this.searchOverlayLoading = false
      }
    },
    closeSearchOverlay() {
      this.searchOverlayOpen = false
      this.searchOverlayQuery = ''
      this.searchOverlayResults = []
    },
  },
})
